/**
 * Platform Configuration Context Provider
 *
 * Provides platform configuration, component registry, and route registry
 * to the React component tree.
 */

import React, { createContext, useContext, ReactNode } from "react";

import { FeatureFlags } from "../feature-flags/FeatureFlags";

import { ComponentRegistry } from "./ComponentRegistry";
import { PlatformConfigManager } from "./PlatformConfig";
import { RouteRegistry } from "./RouteRegistry";

interface PlatformConfigContextValue {
  platformConfig: PlatformConfigManager | null;
  componentRegistry: ComponentRegistry | null;
  routeRegistry: RouteRegistry | null;
  featureFlags: FeatureFlags;
  isInitialized: boolean;
}

const PlatformConfigContext = createContext<PlatformConfigContextValue | null>(
  null,
);

interface PlatformConfigProviderProps {
  children: ReactNode;
  platformConfig: PlatformConfigManager | null;
  componentRegistry: ComponentRegistry | null;
  routeRegistry: RouteRegistry | null;
  featureFlags: FeatureFlags;
}

export const PlatformConfigProvider: React.FC<PlatformConfigProviderProps> = ({
  children,
  platformConfig,
  componentRegistry,
  routeRegistry,
  featureFlags,
}) => {
  const value: PlatformConfigContextValue = {
    platformConfig,
    componentRegistry,
    routeRegistry,
    featureFlags,
    isInitialized: platformConfig?.isInitialized() || false,
  };

  return (
    <PlatformConfigContext.Provider value={value}>
      {children}
    </PlatformConfigContext.Provider>
  );
};

export const usePlatformConfig = (): PlatformConfigContextValue => {
  const context = useContext(PlatformConfigContext);
  if (!context) {
    throw new Error(
      "usePlatformConfig must be used within a PlatformConfigProvider",
    );
  }
  return context;
};
